import { formatDuration, formatNumber } from '@/lib/format'
import type { EmployeeResponseStat } from '@/types/api'
import { EmptyState } from '@/components/ui/States'
import styles from './EmployeeResponseTable.module.css'

/** Who the row belongs to: the recorded name, a stable id label, or a catch-all. */
function employeeLabel(stat: EmployeeResponseStat): string {
  if (stat.adminName && stat.adminName.trim() !== '') return stat.adminName
  if (stat.adminUserId != null) return `Admin #${stat.adminUserId}`
  return 'Unattributed'
}

/**
 * Response time broken down by the employee who answered — one row each, busiest
 * first (the order the server sends). Each row carries how many they answered,
 * the assign/reject split of that, and how quickly (median and average).
 *
 * The median leads because response times are skewed: one request left overnight
 * drags an average far past what the operator actually experiences, while the
 * median stays the typical wait.
 */
export function EmployeeResponseTable({ data }: { data: EmployeeResponseStat[] }) {
  if (data.length === 0) {
    return (
      <EmptyState
        title="No one answered a request yet"
        description="Assignments and rejections in this period are attributed here."
        compact
      />
    )
  }

  return (
    <div className={styles.scroller}>
      <table className={styles.table}>
        <caption className="visually-hidden">Response time by employee</caption>
        <thead>
          <tr>
            <th scope="col">Employee</th>
            <th scope="col" className={styles.numeric}>
              Answered
            </th>
            <th scope="col" className={styles.numeric}>
              Median
            </th>
            <th scope="col" className={styles.numeric}>
              Average
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((stat) => (
            <tr key={stat.adminUserId ?? `unattributed-${employeeLabel(stat)}`}>
              <td className={styles.name}>{employeeLabel(stat)}</td>
              <td className={styles.numeric} data-label="Answered">
                <span className={styles.count}>{formatNumber(stat.respondedCount)}</span>
                <span className={styles.split}>
                  {formatNumber(stat.assignedCount)} assigned · {formatNumber(stat.rejectedCount)} rejected
                </span>
              </td>
              <td className={styles.numeric} data-label="Median">
                {formatDuration(stat.medianSeconds)}
              </td>
              <td className={styles.numeric} data-label="Average">
                {formatDuration(stat.averageSeconds)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
